import {
  AddShoppingCartOutlined,
  AutoGraphOutlined,
  CardGiftcardOutlined,
  FactCheckOutlined,
  FireTruckOutlined,
  Inventory2Outlined,
  LibraryAddOutlined,
  MedicalInformationOutlined,
  PaymentOutlined,
  PeopleAltOutlined,
  PercentOutlined,
  PersonAddAlt1Outlined,
  PostAddOutlined,
} from '@mui/icons-material';

import { useSelector, useDispatch } from 'react-redux';
import { decodeToken, secondaryPermission } from '../../utils/settings';
import { useEffect, useState, useCallback } from 'react';
import IconReusable from '../../components/IconReusable';
import { resetBeneficiaryData } from '../../v2/Benefits/Beneficiary/reducer/beneficiaryReducer';

export const MODULE_TO_SECTION = {
  'business': 'MY BUSINESS',
  'humbee-benefits': 'HUMBEE BENEFITS',
  'analytics': 'ANALYTICS',
  'my-network': 'MY NETWORK',
  'onboard-buyer': 'ONBOARD BUYER',
};
export const useFullMenuStructure = () => {
  const dispatch = useDispatch();
  const [tokenData, setTokenData] = useState();
  
  const getTokenData = async () => {
    const decoded = await decodeToken();
    setTokenData(decoded);
  };
  
  useEffect(() => {
    getTokenData();
  }, []);
  
  // Function to clear specific reducer based on module
  const clearReducerByModule = useCallback((moduleKey) => {
    switch (moduleKey) {
      case 'business':
        dispatch({ type: 'CLEAR_RETAILERS' });
        dispatch({ type: 'CLEAR_ORDER' });
        dispatch({ type: 'CLEAR_CART' });
        break;
      case 'humbee-benefits':
        // Only reset nominee data, preserve beneficiary data during navigation
        dispatch(resetBeneficiaryData());
        // Keep beneficiary data intact: dispatch({ type: 'RESET_BENEFICIARY_DATA' });
        break;
      case 'onboard-buyer':
        dispatch({ type: 'CLEAR_RETAILERS' });
        break;
      case 'analytics':
        dispatch({ type: 'RESET_FMCG_REDUCER' });
        dispatch({ type: 'RESET_BCM_ORDER' });
        break;
      default:
        console.warn(`No reducer clearing defined for module: ${moduleKey}`);
    }
  }, [dispatch]);

  // Function to clear all reducers
  const clearAllReducers = useCallback(() => {
    dispatch({ type: 'CLEAR_RETAILERS' });
    dispatch({ type: 'CLEAR_ORDER' });
    dispatch({ type: 'CLEAR_CART' });
    dispatch({ type: 'SEC_CLEAR_CART' });
    dispatch({ type: 'RESET_FMCG_REDUCER' });
    dispatch({ type: 'RESET_BCM_ORDER' });
    dispatch({ type: 'RESET_ALL_BENEFITS' });
    dispatch({ type: 'RESET_SHOP_ADDRESS_REDUCER' });
  }, [dispatch]);

  // Function to clear reducer for specific section
  const clearReducerBySection = useCallback((sectionName) => {
    switch (sectionName) {
      case 'MY BUSINESS':
        dispatch({ type: 'CLEAR_RETAILERS' });
        dispatch({ type: 'CLEAR_ORDER' });
        dispatch({ type: 'CLEAR_CART' });
        break;
      case 'HUMBEE BENEFITS':
        dispatch({ type: 'RESET_ALL_BENEFITS' });
        break;
      case 'MY NETWORK':
        dispatch({ type: '' });
        break;
      case 'ANALYTICS':
        dispatch({ type: 'RESET_FMCG_REDUCER' });
        dispatch({ type: 'RESET_BCM_ORDER' });
        break;
      default:
        console.warn(`No reducer clearing defined for section: ${sectionName}`);
    }
  }, [dispatch]);
    
  let isPwa = false;
  if (tokenData && tokenData?.pwa_module) {
    isPwa = true;
  }
  const { secondaryPermissionsRoles } = useSelector((state) => ({
    secondaryPermissionsRoles: state.loginReducer.secondaryPermissionsRoles,
  }));

  const ORDER_ADMIN = secondaryPermissionsRoles?.includes(secondaryPermission['ORDER_ADMIN']);
  const PAYMENT_ADMIN = secondaryPermissionsRoles?.includes(secondaryPermission['PAYMENT_ADMIN']);
  const COMPANY_ADMIN = secondaryPermissionsRoles?.includes(secondaryPermission['COMPANY_ADMIN']);

  const fullMenuStructure = [
    {
      section: 'MY BUSINESS',
      items: [
        {
          label: 'Create PO',
          key: 'create_order',
          path: '/create-po',
          icon: <AddShoppingCartOutlined />,
          isVisible: !isPwa ? ORDER_ADMIN || COMPANY_ADMIN : true,
          title: 'Create PO',
          moduleKey: 'business',
          sectionName: MODULE_TO_SECTION.business,
          clearReducer: () => clearReducerByModule('business'),
        },
        {
          label: 'Purchase Order',
          key: 'purchase_order',
          path: '/po-list',
          icon: <PostAddOutlined />,
          isVisible: !isPwa ? PAYMENT_ADMIN || ORDER_ADMIN || COMPANY_ADMIN: true,
          title: 'Purchase Order',
          moduleKey: 'business',
          clearReducer: () => clearReducerByModule('business'),
        },
        {
          label: 'Dispatch Status',
          key: 'dispatch_order',
          path: '/order-booking/dispatch-status-list',
          icon: <FireTruckOutlined />,
          isVisible: !isPwa ? ORDER_ADMIN || PAYMENT_ADMIN || COMPANY_ADMIN : true,
          title: 'Dispatch Status',
          moduleKey: 'business',
          clearReducer: () => clearReducerByModule('business'),
        },
        {
          label: 'Secondary Order',
          key: 'secondary_order',
          path: '/order-booking/secondar-orders-list',
          icon: <LibraryAddOutlined />,
          isVisible:!isPwa ? ORDER_ADMIN || COMPANY_ADMIN : true,
          title: 'Secondary Order',
          moduleKey: 'business',
          clearReducer: () => clearReducerByModule('business'),
        },
        {
          label: 'Inventory',
          key: 'inventory',
          path: '/inventory',
          icon: <Inventory2Outlined />,
          isVisible: !isPwa ? PAYMENT_ADMIN || ORDER_ADMIN || COMPANY_ADMIN : true,
          title: 'Inventory',
          moduleKey: 'business',
          clearReducer: () => clearReducerByModule('business'),
        },
        {
          label: 'Payment',
          key: 'payment',
          path: '/distributor/payment',
          icon: <PaymentOutlined />,
          isVisible: !isPwa ? PAYMENT_ADMIN || COMPANY_ADMIN : true,
          title: 'Payment',
          moduleKey: 'business',
          clearReducer: () => clearReducerByModule('business'),
        },
        {
          label: 'Invoices',
          key: 'invoices',
          path: '/order-booking/completed-invoices',
          icon: <FactCheckOutlined />,
          isVisible: !isPwa ? PAYMENT_ADMIN || ORDER_ADMIN || COMPANY_ADMIN : true,
          title: 'Invoices',
          moduleKey: 'business',
          clearReducer: () => clearReducerByModule('business'),
        },
      ],
      clearSectionReducer: () => clearReducerBySection('MY BUSINESS'),
    },
    {
      section: 'MY NETWORK',
      items: [
        {
          label: 'Onboard Buyer',
          key: 'onboard_buyer',
          path: '/onboard-buyer',
          icon: <PersonAddAlt1Outlined />,
          isVisible: !isPwa ? COMPANY_ADMIN : true,
          moduleKey: 'onboard-buyer',
          clearReducer: () => clearReducerByModule('onboard-buyer'),
        },
        {
          label: 'My Network',
          key: 'my_network',
          path: '/my-network',
          icon: <IconReusable name="Network" size={24} />,
          isVisible: !isPwa ? COMPANY_ADMIN : true,
          moduleKey: 'onboard-buyer',
          clearReducer: () => clearReducerByModule('onboard-buyer'),
        },
      ],
      clearSectionReducer: () => clearReducerBySection('MY NETWORK'),
    },
    {
      section: 'HUMBEE BENEFITS',
      items: [
        {
          label: 'My Benefits',
          key: 'my_benefits',
          path: '/benefits/mybenefits',
          icon: <MedicalInformationOutlined />,
          isVisible: true,
          moduleKey: 'humbee-benefits',
          clearReducer: () => clearReducerByModule('humbee-benefits'),
        },
        {
          label: 'Beneficiary',
          key: 'beneficiary',
          path: '/beneficiary',
          icon: <PeopleAltOutlined />,
          isVisible: true,
          moduleKey: 'humbee-benefits',
          clearReducer: () => clearReducerByModule('humbee-benefits'),
        },
        {
          label: 'Cumulative TDS',
          key: 'cumulative_tds',
          path: '/benefits/cumulative-tds',
          icon: <PercentOutlined />,
          isVisible: true,
          moduleKey: 'humbee-benefits',
          clearReducer: () => clearReducerByModule('humbee-benefits'),
        },
        {
          label: 'My Rewards',
          key: 'my_rewards',
          path: '/benefits/rewards',
          icon: <CardGiftcardOutlined />,
          isVisible: true,
          moduleKey: 'humbee-benefits',
          clearReducer: () => clearReducerByModule('humbee-benefits'),
        },
        {
          label: 'Network Rewards',
          key: 'network_rewards',
          path: '/benefits/network-rewards',
          icon: <CardGiftcardOutlined />,
          isVisible: true,
          moduleKey: 'humbee-benefits',
          clearReducer: () => clearReducerByModule('humbee-benefits'),
        },
      ],
      clearSectionReducer: () => clearReducerBySection('HUMBEE BENEFITS'),
    },
    {
      section: 'ANALYTICS',
      items: [
        {
          label: 'Performance',
          key: 'performance',
          path: '/my-performance/Performance',
          icon: <AutoGraphOutlined />,
          isVisible: true,
          moduleKey: 'analytics',
          clearReducer: () => clearReducerByModule('analytics'),
        },
        {
          label: 'Network Performance',
          key: 'network_performance',
          path: '/my-performance/network-performance',
          icon: <AutoGraphOutlined />,
          isVisible: true,
          moduleKey: 'analytics',
          clearReducer: () => clearReducerByModule('analytics'),
        },
      ],
      clearSectionReducer: () => clearReducerBySection('ANALYTICS'),
    },
  ];
  const filteredMenuStructure = fullMenuStructure
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.isVisible),
    }))
    .filter((section) => section.items.length > 0);

  return {
    menuStructure: filteredMenuStructure,
    clearReducerByModule,
    clearAllReducers,
    clearReducerBySection,
  };
};
